import React, { useMemo } from 'react';
import classNames from 'classnames';
import { DBO_TM, DBO_V } from '@op/utils/format';
import useIntls from '@opm/hooks/useIntls';
import URLShare from '@opm/components/URLShare';
import RenderImg from '@op/components/RenderImg';
import IconPlay from '@opm/assets/svg/icon-play-grey.svg';
import ChannelItem from '@opm/pages/CompetitorsAdCreatives/CompetitorsAdCreativesTable/ChannelItem';
import CountriesList from '@opm/pages/CompetitorsAdCreatives/CompetitorsAdCreativesTable/CountriesList';
import { ITopCreative, ICreativeItem } from '../../types';
import './index.scss';
import { DefaultImg } from '@op/constants/defaultImg'; 

interface IVideoCoverProps {
  /**
   * 数据来源：
   * - ITopCreative：来自 /list 接口的 top_related_creatives（仅 thumbnail）
   * - ICreativeItem：来自 /related_creatives 接口（preview_img + origin_url + ...）
   */
  data: ITopCreative | ICreativeItem;
  /** 点击播放（封面或 play 按钮） */
  onPlay?: (item: ITopCreative | ICreativeItem) => void;
  className?: string;
  filter: any;
}

/**
 * VideoCover 视频缩略图组件（按 Figma I3902_52417 设计实现）
 *
 * 状态：
 * - Default：顶部 Impressions/Likes 遮罩 + 底部日期遮罩
 * - Hover：在 Default 之上叠加半透明黑蒙层 + Share + Play 两个白圆按钮
 */
export default function VideoCover({
  data,
  onPlay,
  className,
  filter,
}: IVideoCoverProps) {
  const { intl } = useIntls();

  // Share URL fallback：video_url
  const shareUrl = data.origin_url || '';
  // 是否展示底部日期范围
  const hasDateRange = !!(data.first_seen && data.last_seen);

  // channel / countries（list.md `top_related_creatives` 已下发）
  // 仅在 data.channel / data.countries 引用变化时重算
  const { channel, countries, hasMeta } = useMemo(() => {
    const { channel = [], countries = [] } = data || {};
    return {
      channel,
      countries,
      hasMeta: channel?.length > 0 || countries?.length > 0,
    };
  }, [(data as any).channel, (data as any).countries]);

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPlay?.(data);
  };

  return (
    <div
      className={classNames('dbom-ct-video-cover', className)}
      // onClick={handlePlay}
    >
      {/* 封面图：失败/无图自动 fallback 到默认图
          - Related Creatives（/related_creatives）→ preview_img
          - Top3 缩略图（/list 内嵌）→ thumbnail */}
      <RenderImg
        className="dbom-ct-video-cover__img"
        coverImage={data.preview_img || ''}
        defaultImage={DefaultImg.VIDEO}
      />

      {/* 顶部遮罩：Impressions / Likes */}
      <div className="dbom-ct-video-cover__top-overlay">
        <div className="dbom-ct-video-cover__stat">
          <div className="dbom-ct-video-cover__stat-label">
            {intl('Impressions')}
          </div>
          <div className="dbom-ct-video-cover__stat-value">
            {DBO_V.reviews(data.impression_number || 0)}
          </div>
        </div>
        <div className="dbom-ct-video-cover__stat">
          <div className="dbom-ct-video-cover__stat-label">{intl('Likes')}</div>
          <div className="dbom-ct-video-cover__stat-value">
            {DBO_V.reviews(data.like_count || 0)}
          </div>
        </div>
      </div>
      {/* 底部遮罩：channel + countries 图标行 + 日期范围（first_seen ~ last_seen） */}
      {(hasMeta || hasDateRange) && (
        <div className="dbom-ct-video-cover__bottom-overlay">
          {hasMeta && (
            <div className="dbom-ct-video-cover__meta">
              {/* 渠道图标 */}
              <ChannelItem
                list={channel}
                maxVisible={1}
                className={countries.length > 0 ? 'dbom-line-divider' : ''}
              />
              {/* 国家图标 */}
              <CountriesList maxVisible={1} countries={countries} />
            </div>
          )}
          {hasDateRange && (
            <span className="dbom-ct-video-cover__date">
              {DBO_TM.timeTitle({
                start_time: data.first_seen || '',
                end_time: data.last_seen || '',
                date_type: filter.date_type || 'daily',
              })}
            </span>
          )}
        </div>
      )}

      {/* Hover 蒙层 + 双按钮 */}
      <div className="dbom-ct-video-cover__hover-mask">
        <div className="dbom-ct-video-cover__actions">
          {/* Play 按钮 */}
          <div className="dbom-ct-video-cover__action-btn" onClick={handlePlay}>
            <span className="dbom-ct-video-cover__action-icon-wrapper">
              <img
                src={IconPlay}
                alt="play"
                className="dbom-ct-video-cover__action-icon"
              />
            </span>
          </div>
          {/* Share 按钮 — 复用 URLShare */}
          <URLShare
            url={shareUrl}
            className={classNames("dbom-ct-video-cover__action-btn", {
              "--no-url": !shareUrl
            })}
            iconClassName="dbom-ct-video-cover__action-icon"
            tips={shareUrl ? "Go to original post" : "No URL"}
          />
        </div>
      </div>
    </div>
  );
}
